import React, { useEffect, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { uniq } from 'lodash';
import Bluebird from 'bluebird';
import { Box } from '@mui/material';
import BaseMUIDataTable from './BaseMUIDataTable/BaseMUIDataTable';
import { formatCsvFileName, formatDateTime } from '../helpers/formatters';
import { DateFilterOptions } from '../helpers/MuiTableCustomFilters';
import { getMuiTableDataIndex } from '../helpers/getters';
import { getUserFullName } from '../helpers/user';
import * as usersApi from '../api/users';
import routes from '../routes';

const actionTypeLabels = {
  DONOR_CREATED: 'Created Donor',
  DONOR_MODIFIED: 'Modified Donor',
  DONOR_DELETED: 'Deleted Donor',
  RECEIVING_AGENCY_CREATED: 'Receiving Agency Created',
  RECEIVING_AGENCY_MODIFIED: 'Receiving Agency Modified',
};

const ItemLogsTable = ({ tableId, title, logs = [] }) => {
  const [usersById, setUsersById] = useState({});
  const userIds = uniq(logs.map((log) => log.user_id).filter(Boolean));

  // crud_actions.user_id is always the real user, even when the change was made while impersonating
  useEffect(() => {
    if (!userIds.length) {
      return;
    }

    Bluebird.try(() => usersApi.getUsers({ narrow: true, ids: userIds }))
      .then((res) => res.json())
      .then((json) => setUsersById(Object.fromEntries((json.data || []).map((user) => [user.id, user]))))
      .catch(() => setUsersById({}));
  }, [userIds.join(',')]);

  const data = logs.map((log) => ({ ...log, user: getUserFullName(usersById[log.user_id]) }));

  const dateCustomBodyRender = (value) => {
    if (!value) {
      return '-';
    }

    return formatDateTime(value, true);
  };

  const typeCustomBodyRender = (value) => actionTypeLabels[value] || value || 'N/A';

  const userCustomBodyRender = (value, tableMeta, csv = false) => {
    if (!value) {
      return 'N/A';
    }

    if (csv) {
      return value;
    }

    const log = data[getMuiTableDataIndex(tableMeta)];

    return (
      <Box
        component={Link}
        sx={{ textDecoration: 'none', cursor: 'pointer', color: 'primary.main' }}
        to={generatePath(routes.userEditNew, { id: log.user_id })}
      >
        {value}
      </Box>
    );
  };

  return (
    <BaseMUIDataTable
      tableId={tableId}
      title={title}
      data={data}
      columns={[
        {
          name: 'id',
          label: 'ID',
          options: {
            filter: false,
            sort: false,
            display: false,
          },
        },
        {
          name: 'created_at',
          label: 'Date',
          options: {
            sort: true,
            filter: true,
            ...DateFilterOptions,
            customBodyRender: (value) => dateCustomBodyRender(value),
            customBodyRenderCSV: (value) => dateCustomBodyRender(value),
          },
        },
        {
          name: 'action_type',
          label: 'Event',
          options: {
            sort: true,
            filterType: 'multiselect',
            customFilterListOptions: {
              render: (value) => `Type: ${typeCustomBodyRender(value)}`,
            },
            customBodyRender: (value) => typeCustomBodyRender(value),
            customBodyRenderCSV: (value) => typeCustomBodyRender(value),
          },
        },
        {
          name: 'user',
          label: 'User',
          options: {
            sort: true,
            filterType: 'multiselect',
            customFilterListOptions: {
              render: (value) => `User: ${value}`,
            },
            customBodyRender: (value, tableMeta) => userCustomBodyRender(value, tableMeta),
            customBodyRenderCSV: (value, tableMeta) => userCustomBodyRender(value, tableMeta, true),
          },
        },
      ]}
      options={{
        selectableRows: 'none',
        responsive: 'simple',
        downloadOptions: {
          filename: formatCsvFileName(title),
        },
        sortOrder: {
          name: 'created_at',
          direction: 'desc',
        },
      }}
    />
  );
};

export default ItemLogsTable;
